import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import {
  ButtonComponent,
  DsChipComponent,
  IconComponent,
  SoftSlotDirective,
  TranslatePipe,
  translator
} from '@softline/ui-core';
import { PageFilterRef } from '../../types/page-filter.ref';
import { PageFilterService } from '../../services/page-filter.service';
import { PageFilterChip } from '../../helper/page-filter-chips.function';
import { FilterOperatorComponent } from '../../../components/filter-operator/filter-operator.component';
import { formatFilterValueSummary, showsOperatorGlyph, summarizeFilterValue } from '../../../helper/format-filter-value.function';

/**
 * The row below the filter bar's widgets: one chip per unpinned item with a committed value
 * - "Title: value" or "Title: N" for several values (see buildPageFilterChips), rendered by
 * soft-page-filter-bar. A chip's text opens
 * the sidebar on its field - value chips on the detail view, toggle chips on their focused
 * list row; without a sidebar (`pageFilter({ sidebar: false })`) the text is static.
 */
@Component({
  selector: 'soft-page-filter-chips',
  imports: [
    NgTemplateOutlet,
    DsChipComponent,
    ButtonComponent,
    IconComponent,
    SoftSlotDirective,
    FilterOperatorComponent,
    TranslatePipe
  ],
  templateUrl: './page-filter-chips.component.html',
  styleUrl: './page-filter-chips.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.is-empty]': 'chips().length === 0' },
})
export class PageFilterChipsComponent {
  private readonly t = translator();

  readonly config = input.required<PageFilterRef>();

  readonly service = computed<PageFilterService>(() => this.config().service);

  readonly chips = computed(() => this.service().chips());

  /** labels resolved once per chips() change instead of per change-detection pass */
  readonly views = computed(() =>
    this.chips().map((chip) => ({
      chip,
      title: `${chip.item.config.title()}`,
      value: this.chipValue(chip),
      showsOperator: this.showsOperator(chip),
      editLabel: this.editLabel(chip),
      removeLabel: this.removeLabel(chip),
      fullText: this.fullText(chip),
    }))
  );

  /** the value part of the chip text; null for toggles and operator-only filters */
  chipValue(chip: PageFilterChip): string | null {
    switch (chip.kind) {
      case 'value':
        return formatFilterValueSummary(
          { kind: 'text', text: chip.item.config.formatter(chip.value as never) },
          this.t
        );
      case 'collapsed':
        return formatFilterValueSummary({ kind: 'count', count: chip.count }, this.t, { countSuffix: false });
      case 'single':
        return formatFilterValueSummary(summarizeFilterValue(chip.item.config, chip.filter), this.t);
      case 'toggle':
        return null;
    }
  }

  /** the untruncated chip text for the tooltip; the operator is spelled out, so a notIn chip
      can never read as an in chip once the glyph is clipped away */
  fullText(chip: PageFilterChip): string {
    const title = `${chip.item.config.title()}`;
    if (chip.kind === 'toggle') return title;
    const value = this.chipValue(chip);
    if (!this.showsOperator(chip)) return value ? `${title}: ${value}` : title;
    // the operator is never dropped - without the translation module the raw name still keeps
    // notIn distinguishable from in, which a bare "Titel: Wert" would not
    const translated = this.t(`#CORE_FILTER.OPERATORS.${chip.filter.operator}`)();
    const operator = translated && !translated.startsWith('#') ? translated : chip.filter.operator;
    return value ? `${title} ${operator}: ${value}` : `${title} ${operator}`;
  }

  showsOperator(chip: PageFilterChip): boolean {
    return showsOperatorGlyph(chip.filter.operator);
  }

  editLabel(chip: PageFilterChip): string {
    return `${this.t('#CORE_FILTER.CHIPS.EDIT')()}: ${chip.item.config.title()}`;
  }

  removeLabel(chip: PageFilterChip): string {
    const title = `${chip.item.config.title()}`;
    // a count is not a value - "Filter löschen: Artikel 7" would read as clearing the value 7
    const value = chip.kind === 'value' || chip.kind === 'single' ? this.chipValue(chip) : null;
    const key = chip.kind === 'value' ? '#CORE_FILTER.CHIPS.REMOVE_VALUE' : '#CORE_FILTER.LIST.CLEAR_FILTER';
    return `${this.t(key)()}: ${value ? `${title} ${value}` : title}`;
  }

  remove(chip: PageFilterChip): void {
    const service = this.service();
    switch (chip.kind) {
      case 'value':
        service.removeItemValue(chip.item, chip.value, 'source');
        break;
      case 'toggle':
        service.toggleItemFilter(chip.item, 'source');
        break;
      default:
        service.applyItemFilter(chip.item, null, 'source');
    }
  }

  openItem(chip: PageFilterChip): void {
    this.config().open({ item: chip.item });
  }
}
